import { useEffect, useRef } from 'react';
import { Application, Container, Graphics, Text, TextStyle, Ticker } from 'pixi.js';
import { ROOMS, CORRIDORS, type Room } from './rooms.ts';
import { useAppStore, type Bot } from '../state/store.ts';

// ─── Colors ───────────────────────────────────────────────────────────────────
const BOARD_RED = 0xff2244;
const GRID_DIM  = 0xff2244;
const HULL_BG   = 0x030508;
const PANEL_BG  = 0x060a10;

// ─── Status maps ──────────────────────────────────────────────────────────────
const STATUS_GLOW: Record<Bot['status'], { alpha: number; color: number }> = {
  idle:              { alpha: 0.07, color: BOARD_RED },
  working:           { alpha: 0.35, color: 0x5aff9f },
  awaiting_approval: { alpha: 0.38, color: 0xffb347 },
  alert:             { alpha: 0.50, color: 0xff4444 },
  paused:            { alpha: 0.04, color: 0x555555 },
};
const STATUS_DOT: Record<Bot['status'], number> = {
  idle:              BOARD_RED,
  working:           0x5aff9f,
  awaiting_approval: 0xffb347,
  alert:             0xff4444,
  paused:            0x444444,
};
const STATUS_LABEL: Record<Bot['status'], string> = {
  idle:              'STANDBY',
  working:           'ACTIVE',
  awaiting_approval: 'WAITING',
  alert:             'ALERT',
  paused:            'OFFLINE',
};

// ─── Dense circuit-grid ───────────────────────────────────────────────────────
function drawGrid(g: Graphics, W: number, H: number) {
  const sm = 16;
  const lg = 64;
  for (let x = 0; x <= W; x += sm) { g.moveTo(x, 0).lineTo(x, H); }
  for (let y = 0; y <= H; y += sm) { g.moveTo(0, y).lineTo(W, y); }
  g.stroke({ width: 0.4, color: GRID_DIM, alpha: 0.07 });
  for (let x = 0; x <= W; x += lg) { g.moveTo(x, 0).lineTo(x, H); }
  for (let y = 0; y <= H; y += lg) { g.moveTo(0, y).lineTo(W, y); }
  g.stroke({ width: 0.7, color: GRID_DIM, alpha: 0.14 });
  for (let x = lg; x < W; x += lg) {
    for (let y = lg; y < H; y += lg) {
      g.circle(x, y, 2).fill({ color: GRID_DIM, alpha: 0.28 });
    }
  }
  const traces: Array<{ x: number; y: number; hLen: number; dir: 1 | -1 }> = [
    { x: lg,      y: lg * 2,  hLen: lg * 3, dir:  1 },
    { x: lg * 5,  y: lg,      hLen: lg * 4, dir:  1 },
    { x: lg * 2,  y: lg * 5,  hLen: lg * 2, dir: -1 },
    { x: lg * 8,  y: lg * 3,  hLen: lg * 3, dir:  1 },
    { x: lg * 4,  y: lg * 7,  hLen: lg * 5, dir:  1 },
    { x: lg * 10, y: lg * 2,  hLen: lg * 2, dir: -1 },
    { x: lg * 6,  y: lg * 9,  hLen: lg * 4, dir:  1 },
    { x: lg * 1,  y: lg * 10, hLen: lg * 3, dir:  1 },
    { x: lg * 9,  y: lg * 6,  hLen: lg * 2, dir: -1 },
    { x: lg * 3,  y: lg * 12, hLen: lg * 6, dir:  1 },
    { x: lg * 11, y: lg * 8,  hLen: lg * 3, dir:  1 },
    { x: lg * 7,  y: lg * 11, hLen: lg * 2, dir: -1 },
  ];
  for (const t of traces) {
    const ex = t.x + t.hLen;
    const ey = t.y + lg * t.dir;
    if (ex < W && ey > 0 && ey < H) {
      g.moveTo(t.x, t.y).lineTo(ex, t.y).lineTo(ex, ey);
      g.stroke({ width: 1, color: GRID_DIM, alpha: 0.13 });
      g.circle(t.x, t.y, 3).fill({ color: GRID_DIM, alpha: 0.22 });
      g.circle(ex, ey, 3).fill({ color: GRID_DIM, alpha: 0.22 });
    }
  }
}

function roomCenter(room: Room, padX: number, padY: number, hullW: number, hullH: number) {
  return {
    x: padX + (room.x + room.w / 2) * hullW,
    y: padY + (room.y + room.h / 2) * hullH,
  };
}

// ─── Corridor data for animation ──────────────────────────────────────────────
interface CorridorPath {
  fromId: string;
  toId: string;
  fc: { x: number; y: number };
  tc: { x: number; y: number };
  mid: { x: number; y: number };
  isActive: boolean;
  totalLen: number;
  seg1Frac: number;
}

function buildCorridorPaths(
  padX: number, padY: number, hullW: number, hullH: number,
  botMap: Map<string, Bot>,
): CorridorPath[] {
  const roomMap = new Map(ROOMS.map((r) => [r.id, r]));
  const paths: CorridorPath[] = [];
  for (const [fromId, toId] of CORRIDORS) {
    const from = roomMap.get(fromId);
    const to   = roomMap.get(toId);
    if (!from || !to) continue;
    const fc  = roomCenter(from, padX, padY, hullW, hullH);
    const tc  = roomCenter(to,   padX, padY, hullW, hullH);
    const mid = { x: fc.x + (tc.x - fc.x) * 0.5, y: fc.y };
    const seg1 = Math.abs(mid.x - fc.x);
    const seg2 = Math.abs(mid.y - tc.y);
    const seg3 = Math.abs(tc.x - mid.x);
    const total = seg1 + seg2 + seg3;
    const fromStatus = botMap.get(fromId)?.status ?? 'idle';
    const toStatus   = botMap.get(toId)?.status   ?? 'idle';
    paths.push({
      fromId, toId, fc, tc, mid,
      isActive: fromStatus === 'working' || toStatus === 'working',
      totalLen: total,
      seg1Frac: total > 0 ? seg1 / total : 0.33,
    });
  }
  return paths;
}

function posOnPath(cp: CorridorPath, t: number): { x: number; y: number } {
  const seg1Frac = cp.seg1Frac;
  const seg2Frac = cp.totalLen > 0
    ? Math.abs(cp.mid.y - cp.tc.y) / cp.totalLen
    : 0.34;
  if (t < seg1Frac) {
    const u = t / seg1Frac;
    return { x: cp.fc.x + (cp.mid.x - cp.fc.x) * u, y: cp.fc.y };
  } else if (t < seg1Frac + seg2Frac) {
    const u = (t - seg1Frac) / seg2Frac;
    return { x: cp.mid.x, y: cp.fc.y + (cp.tc.y - cp.fc.y) * u };
  } else {
    const rem = 1 - seg1Frac - seg2Frac;
    const u = rem > 0 ? (t - seg1Frac - seg2Frac) / rem : 1;
    return { x: cp.mid.x + (cp.tc.x - cp.mid.x) * u, y: cp.tc.y };
  }
}

// ─── Static corridor draw ─────────────────────────────────────────────────────
function drawCorridors(
  scene: Container,
  padX: number, padY: number, hullW: number, hullH: number,
  botMap: Map<string, Bot>,
) {
  const roomMap = new Map(ROOMS.map((r) => [r.id, r]));
  for (const [fromId, toId] of CORRIDORS) {
    const from = roomMap.get(fromId);
    const to   = roomMap.get(toId);
    if (!from || !to) continue;
    const fc  = roomCenter(from, padX, padY, hullW, hullH);
    const tc  = roomCenter(to,   padX, padY, hullW, hullH);
    const fromStatus = botMap.get(fromId)?.status ?? 'idle';
    const toStatus   = botMap.get(toId)?.status   ?? 'idle';
    const isActive  = fromStatus === 'working' || toStatus === 'working';
    const lineAlpha = isActive ? 0.45 : 0.18;
    const lineColor = isActive ? 0xff4466 : BOARD_RED;
    const lineWidth = isActive ? 2 : 1;
    const midX = fc.x + (tc.x - fc.x) * 0.5;
    const g = new Graphics();
    g.moveTo(fc.x, fc.y).lineTo(midX, fc.y).lineTo(midX, tc.y).lineTo(tc.x, tc.y)
      .stroke({ width: lineWidth, color: lineColor, alpha: lineAlpha });
    g.circle(midX, fc.y, 3.5).fill({ color: lineColor, alpha: lineAlpha + 0.1 });
    g.circle(midX, tc.y, 3.5).fill({ color: lineColor, alpha: lineAlpha + 0.1 });
    g.circle(fc.x, fc.y, 2.5).fill({ color: lineColor, alpha: 0.5 });
    g.circle(tc.x, tc.y, 2.5).fill({ color: lineColor, alpha: 0.5 });
    scene.addChild(g);
  }
}

// ─── Bloom glow ───────────────────────────────────────────────────────────────
function makeBloomGraphics(
  rx: number, ry: number, rw: number, rh: number,
  color: number, alpha: number,
): Graphics[] {
  const result: Graphics[] = [];
  for (let i = 3; i >= 1; i--) {
    const spread = i * 14;
    const g = new Graphics();
    g.roundRect(rx - spread, ry - spread, rw + spread * 2, rh + spread * 2, 12 + spread)
      .fill({ color, alpha: (alpha / 3) * (4 - i) * 0.5 });
    result.push(g);
  }
  return result;
}

// ─── Room interior detail ──────────────────────────────────────────────────────
function drawRoomInterior(
  scene: Container,
  rx: number, ry: number, rw: number, rh: number,
  accent: number, status: Bot['status'],
) {
  const g = new Graphics();
  const dotColor = STATUS_DOT[status];
  const step = 12;
  for (let x = rx + step; x < rx + rw - 2; x += step) { g.moveTo(x, ry + 28).lineTo(x, ry + rh - 14); }
  for (let y = ry + 28 + step; y < ry + rh - 14; y += step) { g.moveTo(rx + 4, y).lineTo(rx + rw - 4, y); }
  g.stroke({ width: 0.4, color: accent, alpha: 0.12 });
  const barX = rx + rw - 32; const barY = ry + 8;
  for (let i = 0; i < 3; i++) {
    const bw = [20, 14, 18][i];
    g.roundRect(barX, barY + i * 5, bw, 2.5, 1).fill({ color: accent, alpha: 0.55 - i * 0.1 });
  }
  for (let i = 0; i < 4; i++) {
    const active = status === 'working' ? i < 3 : status === 'awaiting_approval' ? i < 2 : i < 1;
    g.roundRect(rx + 8 + i * 9, ry + rh - 11, 6, 6, 1)
      .fill({ color: active ? dotColor : 0x1a2030, alpha: active ? 0.9 : 0.6 });
    if (active) {
      g.roundRect(rx + 8 + i * 9 - 1, ry + rh - 12, 8, 8, 2)
        .stroke({ width: 0.5, color: dotColor, alpha: 0.4 });
    }
  }
  const waveX = rx + rw - 40; const waveY = ry + rh - 9;
  const pts = status === 'working'
    ? [0, -4, 2, -4, 2, 4, 6, 4, 6, -2, 10, -2, 10, 3, 14, 3, 14, 0, 18, 0]
    : [0, 0, 4, 0, 4, -2, 8, -2, 8, 0, 12, 0, 16, 0, 20, 0, 24, 0];
  for (let i = 0; i < pts.length - 2; i += 2) {
    if (i === 0) g.moveTo(waveX + pts[i], waveY + pts[i + 1]);
    else g.lineTo(waveX + pts[i], waveY + pts[i + 1]);
  }
  g.stroke({ width: 1, color: dotColor, alpha: 0.5 });
  const spW = rw * 0.45; const spH = rh * 0.28;
  const spX = rx + (rw - spW) / 2; const spY = ry + rh * 0.32;
  g.roundRect(spX, spY, spW, spH, 3)
    .fill({ color: 0x040710, alpha: 0.8 })
    .stroke({ width: 0.8, color: accent, alpha: 0.35 });
  for (let i = 1; i < 3; i++) {
    g.moveTo(spX + 4, spY + (spH / 3) * i).lineTo(spX + spW - 4, spY + (spH / 3) * i);
  }
  g.stroke({ width: 0.4, color: accent, alpha: 0.20 });
  scene.addChild(g);
}

// ─── Animation state types ────────────────────────────────────────────────────
interface PulseDot {
  g: Graphics;
  t: number;               // 0..1 position along path
  cp: CorridorPath;
}

interface RoomAnim {
  bloomGraphics: Graphics[];  // 3 bloom layers, alpha driven by sine
  baseAlpha: number;
  peakAlpha: number;
  phase: number;              // initial phase offset (radians)
  period: number;             // seconds per full cycle
  tickContainer: Container;   // rotates each frame
  status: Bot['status'];
}

// ─── Main draw (static pass, returns anim handles) ────────────────────────────
interface DrawResult {
  roomAnims: RoomAnim[];
  corridorPaths: CorridorPath[];
}

function drawScene(
  scene: Container,
  W: number, H: number,
  botMap: Map<string, Bot>,
  onRoomClick: (roomId: string) => void,
): DrawResult {
  scene.removeChildren();

  const padX = W * 0.03; const padY = H * 0.03;
  const hullW = W - padX * 2; const hullH = H - padY * 2;

  // Grid
  const gridG = new Graphics();
  drawGrid(gridG, W, H);
  scene.addChild(gridG);

  // Hull plate
  const hull = new Graphics();
  hull.roundRect(padX, padY, hullW, hullH, 8)
    .fill({ color: HULL_BG, alpha: 0.88 })
    .stroke({ width: 2, color: BOARD_RED, alpha: 0.70 });
  scene.addChild(hull);

  const hm = new Graphics();
  hm.roundRect(padX + 6, padY + 6, hullW - 12, hullH - 12, 5)
    .stroke({ width: 0.5, color: BOARD_RED, alpha: 0.18 });
  scene.addChild(hm);

  const cSize = 16; const cg = new Graphics();
  for (const [cx, cy] of [[padX, padY], [padX + hullW - cSize, padY], [padX, padY + hullH - cSize], [padX + hullW - cSize, padY + hullH - cSize]] as [number, number][]) {
    cg.rect(cx, cy, cSize, cSize).fill({ color: BOARD_RED, alpha: 0.18 });
    cg.rect(cx + 1, cy + 1, cSize - 2, cSize - 2).fill({ color: HULL_BG, alpha: 1 });
  }
  scene.addChild(cg);

  const spine = new Graphics();
  spine.moveTo(W / 2, padY + 8).lineTo(W / 2, padY + hullH - 8)
    .stroke({ width: 1, color: BOARD_RED, alpha: 0.20 });
  scene.addChild(spine);

  const hspine = new Graphics();
  hspine.moveTo(padX + 8, H / 2).lineTo(padX + hullW - 8, H / 2)
    .stroke({ width: 1, color: BOARD_RED, alpha: 0.12 });
  scene.addChild(hspine);

  // Corridors
  drawCorridors(scene, padX, padY, hullW, hullH, botMap);

  // Corridor paths for pulse animation
  const corridorPaths = buildCorridorPaths(padX, padY, hullW, hullH, botMap);

  // Text styles
  const labelStyle = new TextStyle({ fontFamily: 'JetBrains Mono, Menlo, monospace', fontSize: 9, fontWeight: '700', fill: 0xe0e8ff, letterSpacing: 2.5 });
  const botStyle = new TextStyle({ fontFamily: 'JetBrains Mono, Menlo, monospace', fontSize: 10, fontWeight: '600', fill: 0xffffff, letterSpacing: 0.5 });
  const statusStyle = new TextStyle({ fontFamily: 'JetBrains Mono, Menlo, monospace', fontSize: 8, fill: 0xaabbcc, letterSpacing: 1.5 });
  const coordStyle = new TextStyle({ fontFamily: 'JetBrains Mono, Menlo, monospace', fontSize: 7, fill: 0x446688, letterSpacing: 0.5 });

  const roomAnims: RoomAnim[] = [];

  ROOMS.forEach((room: Room, idx: number) => {
    const bot    = botMap.get(room.id);
    const status = bot?.status ?? 'idle';
    const glow   = STATUS_GLOW[status];
    const dotColor = STATUS_DOT[status];
    const rx = padX + room.x * hullW; const ry = padY + room.y * hullH;
    const rw = room.w * hullW; const rh = room.h * hullH;

    // Bloom glow layers — kept as refs for animation
    const bloomColor = status === 'idle' ? room.accent : glow.color;
    const bloomAlpha = status === 'idle' ? 0.12 : glow.alpha * 1.4;
    const bloomGraphics = makeBloomGraphics(rx, ry, rw, rh, bloomColor, bloomAlpha);
    for (const bg of bloomGraphics) scene.addChild(bg);

    // Room base plate
    const plate = new Graphics();
    plate.rect(rx, ry, rw, rh).fill({ color: PANEL_BG, alpha: 0.96 });
    scene.addChild(plate);

    const fill = new Graphics();
    fill.rect(rx + 1, ry + 1, rw - 2, rh - 2).fill({ color: glow.color, alpha: glow.alpha });
    scene.addChild(fill);

    drawRoomInterior(scene, rx, ry, rw, rh, room.accent, status);

    const borderColor = status === 'idle' ? room.accent : dotColor;
    const borderAlpha = status === 'idle' ? 0.65 : 1.0;
    const borderW     = status === 'awaiting_approval' || status === 'alert' ? 2.5 : 1.5;
    const border = new Graphics();
    border.rect(rx, ry, rw, rh).stroke({ width: borderW, color: borderColor, alpha: borderAlpha });
    scene.addChild(border);
    border.rect(rx + 3, ry + 3, rw - 6, rh - 6).stroke({ width: 0.5, color: borderColor, alpha: 0.22 });
    scene.addChild(border);

    const brk = new Graphics(); const b = 12;
    for (const [cx, cy, dx, dy] of [[rx, ry, 1, 1], [rx + rw, ry, -1, 1], [rx, ry + rh, 1, -1], [rx + rw, ry + rh, -1, -1]] as [number, number, number, number][]) {
      brk.moveTo(cx + dx * b, cy).lineTo(cx, cy).lineTo(cx, cy + dy * b);
      brk.circle(cx, cy, 2).fill({ color: borderColor, alpha: 1 });
    }
    brk.stroke({ width: 2, color: borderColor, alpha: 1 });
    scene.addChild(brk);

    const topBar = new Graphics();
    topBar.rect(rx, ry, rw, 22).fill({ color: borderColor, alpha: 0.18 });
    scene.addChild(topBar);

    const label = new Text({ text: room.name.toUpperCase(), style: labelStyle });
    label.x = rx + 8; label.y = ry + 7;
    scene.addChild(label);

    const coordStr = `${Math.round(room.x * 100)}.${Math.round(room.y * 100)}`;
    const coord = new Text({ text: coordStr, style: coordStyle });
    coord.x = rx + rw - coord.width - 6; coord.y = ry + 8;
    scene.addChild(coord);

    // Bot circle — cx/cy center
    const cx = rx + rw / 2; const cy = ry + rh * 0.55;

    // Outer ring fill + stroke
    const ring = new Graphics();
    ring.circle(cx, cy, 20).fill({ color: dotColor, alpha: 0.10 });
    ring.circle(cx, cy, 20).stroke({ width: 1, color: dotColor, alpha: 0.40 });
    ring.circle(cx, cy, 8).fill({ color: dotColor, alpha: 0.85 });
    scene.addChild(ring);

    // Tick ring — in its own Container so we can rotate it
    const tickCont = new Container();
    tickCont.x = cx; tickCont.y = cy;
    const tickG = new Graphics();
    for (let a = 0; a < Math.PI * 2; a += Math.PI / 6) {
      const r1 = 22; const r2 = 26;
      tickG.moveTo(Math.cos(a) * r1, Math.sin(a) * r1)
           .lineTo(Math.cos(a) * r2, Math.sin(a) * r2);
    }
    tickG.stroke({ width: 0.8, color: dotColor, alpha: 0.35 });
    tickCont.addChild(tickG);
    scene.addChild(tickCont);

    const botName = new Text({ text: room.bot.toUpperCase(), style: botStyle });
    botName.style.fill = dotColor; botName.x = cx - botName.width / 2; botName.y = cy + 24;
    scene.addChild(botName);

    const sl = new Text({ text: STATUS_LABEL[status], style: statusStyle });
    sl.style.fill = status === 'idle' ? 0x446688 : dotColor;
    sl.x = cx - sl.width / 2; sl.y = cy + 36;
    scene.addChild(sl);

    const hit = new Graphics();
    hit.rect(rx, ry, rw, rh).fill({ color: 0x000000, alpha: 0 });
    hit.eventMode = 'static'; hit.cursor = 'pointer';
    hit.on('pointerdown', () => onRoomClick(room.id));
    scene.addChild(hit);

    // Store anim data
    const isActive = status === 'working' || status === 'alert' || status === 'awaiting_approval';
    roomAnims.push({
      bloomGraphics,
      baseAlpha:  isActive ? bloomAlpha * 0.7  : bloomAlpha * 0.6,
      peakAlpha:  isActive ? bloomAlpha * 1.5  : bloomAlpha * 1.2,
      phase:      (idx / ROOMS.length) * Math.PI * 2,
      period:     isActive ? 1.2 : 3.5,
      tickContainer: tickCont,
      status,
    });
  });

  const hudStyle = new TextStyle({ fontFamily: 'JetBrains Mono, Menlo, monospace', fontSize: 8, fill: BOARD_RED, letterSpacing: 2 });
  const hud1 = new Text({ text: 'THE·HORIZON  //  OPS-BOARD  v1.0', style: hudStyle });
  hud1.x = padX + 10; hud1.y = padY - 14;
  if (hud1.y > 0) scene.addChild(hud1);
  const hud2 = new Text({ text: `BOTS·ONLINE: ${ROOMS.length}`, style: hudStyle });
  hud2.x = padX + hullW - hud2.width - 10; hud2.y = padY + hullH + 4;
  if (hud2.y < H) scene.addChild(hud2);

  return { roomAnims, corridorPaths };
}

// ─── CRT overlay ──────────────────────────────────────────────────────────────
function ensureCrtOverlay(host: HTMLElement) {
  if (host.querySelector('.crt-overlay')) return;
  if (!document.getElementById('crt-styles')) {
    const s = document.createElement('style');
    s.id = 'crt-styles';
    s.textContent = `
      @keyframes scanBarMove {
        0%   { top: -2%; opacity: 0; }
        3%   { opacity: 0.7; }
        97%  { opacity: 0.7; }
        100% { top: 102%; opacity: 0; }
      }
      .crt-scanbar {
        position: absolute; left: 0; width: 100%; height: 2px;
        background: linear-gradient(transparent, rgba(255,34,68,0.18), transparent);
        pointer-events: none; z-index: 20;
        animation: scanBarMove 5s linear infinite;
      }
      .crt-overlay {
        position: absolute; inset: 0; pointer-events: none; z-index: 19;
        background: repeating-linear-gradient(0deg, transparent 0px, transparent 3px, rgba(0,0,0,0.10) 3px, rgba(0,0,0,0.10) 4px);
      }
    `;
    document.head.appendChild(s);
  }
  const overlay = document.createElement('div');
  overlay.className = 'crt-overlay';
  host.appendChild(overlay);
  const bar = document.createElement('div');
  bar.className = 'crt-scanbar';
  host.appendChild(bar);
}

// ─── Component ────────────────────────────────────────────────────────────────
export function ShipCanvas() {
  const hostRef  = useRef<HTMLDivElement | null>(null);
  const appRef   = useRef<Application | null>(null);
  const sceneRef = useRef<Container | null>(null);
  const bots = useAppStore((s) => s.bots);

  // ── Initial mount ──
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const app = new Application();
    let destroyed = false;

    (async () => {
      await app.init({
        resizeTo: host, background: HULL_BG,
        antialias: true, autoDensity: true,
        resolution: window.devicePixelRatio || 1,
      });
      if (destroyed) { app.destroy(true); return; }
      host.style.position = 'relative';
      host.appendChild(app.canvas);
      ensureCrtOverlay(host);

      const scene = new Container();
      app.stage.addChild(scene);
      appRef.current  = app;
      sceneRef.current = scene;

      const ro = new ResizeObserver(() => {
        app.renderer.resize(host.clientWidth, host.clientHeight);
        useAppStore.getState().refreshAll().catch(() => {});
      });
      ro.observe(host);
      (app as unknown as { __ro?: ResizeObserver }).__ro = ro;

      const botMap = new Map(useAppStore.getState().bots.map((b) => [b.room, b]));
      const { width, height } = app.renderer;
      const dpr = window.devicePixelRatio || 1;
      const { roomAnims, corridorPaths } = drawScene(scene, width / dpr, height / dpr, botMap, () => {});

      // Anim layer for pulse dots (on top of everything)
      const animLayer = new Container();
      app.stage.addChild(animLayer);
      (app as unknown as { __animLayer?: Container }).__animLayer = animLayer;

      // Build pulse dots — one per corridor
      const pulseDots: PulseDot[] = corridorPaths.map((cp, i) => {
        const g = new Graphics();
        animLayer.addChild(g);
        return { g, t: i / corridorPaths.length, cp };
      });

      // Room phase time accumulators
      const roomPhaseTime = roomAnims.map((ra) => ra.phase);

      // Ticker
      const tickFn = (ticker: Ticker) => {
        const dt = ticker.deltaMS / 1000;

        // ── Pulse dots ──
        for (const pd of pulseDots) {
          const speed = pd.cp.isActive ? 0.4 : 0.12;
          pd.t = (pd.t + speed * dt) % 1;
          const pos = posOnPath(pd.cp, pd.t);
          const color  = pd.cp.isActive ? 0xff4466 : BOARD_RED;
          const radius = pd.cp.isActive ? 4 : 2.5;
          const alpha  = pd.cp.isActive ? 0.85 : 0.4;
          pd.g.clear();
          // Soft glow behind dot
          pd.g.circle(pos.x, pos.y, radius * 2.5).fill({ color, alpha: alpha * 0.25 });
          pd.g.circle(pos.x, pos.y, radius).fill({ color, alpha });
        }

        // ── Room glow breathe + tick ring rotation ──
        for (let i = 0; i < roomAnims.length; i++) {
          const ra = roomAnims[i];
          roomPhaseTime[i] += dt;
          const cycle = (Math.sin((roomPhaseTime[i] / ra.period) * Math.PI * 2) + 1) / 2;
          const alpha = ra.baseAlpha + (ra.peakAlpha - ra.baseAlpha) * cycle;

          // Update each bloom layer alpha proportionally
          for (let li = 0; li < ra.bloomGraphics.length; li++) {
            const layerAlpha = (alpha / 3) * (4 - (3 - li)) * 0.5;
            ra.bloomGraphics[li].alpha = Math.max(0, layerAlpha);
          }

          // Rotate tick ring
          const rotSpeed = ra.status === 'working' ? (Math.PI * 2) / 2
                         : ra.status === 'alert'   ? (Math.PI * 2) / 1.5
                         : (Math.PI * 2) / 8;
          ra.tickContainer.rotation += rotSpeed * dt;
        }
      };

      app.ticker.add(tickFn);
      (app as unknown as { __tickFn?: (t: Ticker) => void }).__tickFn = tickFn;
    })();

    return () => {
      destroyed = true;
      const ro = (app as unknown as { __ro?: ResizeObserver }).__ro;
      ro?.disconnect();
      const tickFn = (app as unknown as { __tickFn?: (t: Ticker) => void }).__tickFn;
      if (tickFn) app.ticker.remove(tickFn);
      app.destroy(true, { children: true });
      appRef.current  = null;
      sceneRef.current = null;
      if (host) host.innerHTML = '';
    };
  }, []);

  // ── Re-draw when bots change ──
  useEffect(() => {
    const app   = appRef.current;
    const scene = sceneRef.current;
    if (!app || !scene) return;

    // Remove old ticker
    const oldTickFn = (app as unknown as { __tickFn?: (t: Ticker) => void }).__tickFn;
    if (oldTickFn) app.ticker.remove(oldTickFn);

    // Remove old anim layer
    const oldAnimLayer = (app as unknown as { __animLayer?: Container }).__animLayer;
    if (oldAnimLayer) app.stage.removeChild(oldAnimLayer);

    const botMap = new Map(bots.map((b) => [b.room, b]));
    const { width, height } = app.renderer;
    const dpr = window.devicePixelRatio || 1;

    const onRoomClick = (roomId: string) => {
      const tabMap: Record<string, string> = {
        'sensor-array':   'crew',
        'bridge':         'listings',
        'cargo-bay':      'listings',
        'comms-deck':     'messages',
        'storefront-bay': 'storefront',
        'design-studio':  'storefront',
      };
      window.dispatchEvent(new CustomEvent('horizon:room-click', {
        detail: { roomId, tab: tabMap[roomId] ?? 'crew' },
      }));
    };

    const { roomAnims, corridorPaths } = drawScene(scene, width / dpr, height / dpr, botMap, onRoomClick);

    const animLayer = new Container();
    app.stage.addChild(animLayer);
    (app as unknown as { __animLayer?: Container }).__animLayer = animLayer;

    const pulseDots: PulseDot[] = corridorPaths.map((cp, i) => {
      const g = new Graphics();
      animLayer.addChild(g);
      return { g, t: i / corridorPaths.length, cp };
    });

    const roomPhaseTime = roomAnims.map((ra) => ra.phase);

    const tickFn = (ticker: Ticker) => {
      const dt = ticker.deltaMS / 1000;
      for (const pd of pulseDots) {
        const speed = pd.cp.isActive ? 0.4 : 0.12;
        pd.t = (pd.t + speed * dt) % 1;
        const pos    = posOnPath(pd.cp, pd.t);
        const color  = pd.cp.isActive ? 0xff4466 : BOARD_RED;
        const radius = pd.cp.isActive ? 4 : 2.5;
        const alpha  = pd.cp.isActive ? 0.85 : 0.4;
        pd.g.clear();
        pd.g.circle(pos.x, pos.y, radius * 2.5).fill({ color, alpha: alpha * 0.25 });
        pd.g.circle(pos.x, pos.y, radius).fill({ color, alpha });
      }
      for (let i = 0; i < roomAnims.length; i++) {
        const ra = roomAnims[i];
        roomPhaseTime[i] += dt;
        const cycle = (Math.sin((roomPhaseTime[i] / ra.period) * Math.PI * 2) + 1) / 2;
        const alpha = ra.baseAlpha + (ra.peakAlpha - ra.baseAlpha) * cycle;
        for (let li = 0; li < ra.bloomGraphics.length; li++) {
          ra.bloomGraphics[li].alpha = Math.max(0, (alpha / 3) * (4 - (3 - li)) * 0.5);
        }
        const rotSpeed = ra.status === 'working' ? (Math.PI * 2) / 2
                       : ra.status === 'alert'   ? (Math.PI * 2) / 1.5
                       : (Math.PI * 2) / 8;
        ra.tickContainer.rotation += rotSpeed * dt;
      }
    };

    app.ticker.add(tickFn);
    (app as unknown as { __tickFn?: (t: Ticker) => void }).__tickFn = tickFn;
  }, [bots]);

  return <div ref={hostRef} className="absolute inset-0" />;
}
